<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

interface Step {
  labelKey: string
  delay: number
  done: boolean
  visible: boolean
}

const steps = ref<Step[]>([
  { labelKey: 'demo.loading.step1', delay: 500, done: false, visible: false },
  { labelKey: 'demo.loading.step2', delay: 2000, done: false, visible: false },
  { labelKey: 'demo.loading.step3', delay: 3500, done: false, visible: false },
  { labelKey: 'demo.loading.step4', delay: 5000, done: false, visible: false },
  { labelKey: 'demo.loading.step5', delay: 6500, done: false, visible: false },
  { labelKey: 'demo.loading.step6', delay: 7500, done: false, visible: false },
  { labelKey: 'demo.loading.step7', delay: 8500, done: false, visible: false },
  { labelKey: 'demo.loading.step8', delay: 9500, done: false, visible: false },
])

const timers: ReturnType<typeof setTimeout>[] = []

onMounted(() => {
  steps.value.forEach((step, index) => {
    const t1 = setTimeout(() => {
      steps.value[index].visible = true
      const t2 = setTimeout(() => {
        steps.value[index].done = true
      }, 300)
      timers.push(t2)
    }, step.delay)
    timers.push(t1)
  })
})

onUnmounted(() => {
  timers.forEach((t) => clearTimeout(t))
})
</script>

<template>
  <div class="demo-loading-bg fixed inset-0 z-[100] flex flex-col items-center justify-center px-6 py-10 overflow-auto">
    <div class="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
      <div class="mb-8 text-center">
        <img src="/sportovo_logo_dark.png" alt="sportovo.io" class="h-10 mx-auto mb-6">
        <h1 class="text-xl font-semibold text-gray-900 mb-2">{{ t('demo.loading.title') }}</h1>
        <p class="text-sm text-gray-500">{{ t('demo.loading.subtitle') }}</p>
      </div>

      <!-- Progress bar -->
      <div class="w-full bg-gray-200 rounded-full h-2 mb-8 overflow-hidden">
        <div
          class="h-2 bg-blue-600 rounded-full"
          style="animation: progressBar 10s linear forwards"
        ></div>
      </div>

      <!-- Steps list -->
      <ul class="space-y-3">
        <li
          v-for="(step, index) in steps"
          :key="index"
          class="flex items-center gap-3 transition-all duration-300"
          :class="step.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'"
        >
          <div class="flex-shrink-0 w-5 h-5 flex items-center justify-center">
            <svg
              v-if="step.done"
              class="w-5 h-5 text-green-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
            </svg>
            <div v-else class="w-4 h-4 border-2 border-blue-300 rounded-full animate-spin border-t-blue-600"></div>
          </div>
          <span class="text-sm text-gray-700">{{ t(step.labelKey) }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.demo-loading-bg {
  background: linear-gradient(135deg, #f0f5ff 0%, #e0eaff 30%, #f5f0ff 70%, #fdf2f8 100%);
}
@keyframes progressBar {
  from { width: 0% }
  to { width: 100% }
}
</style>
